import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { GlobalStore } from '../../store';
import User from '../../models/User';
import PageNotFound from '../NoMatch';
import { HTTP_SERVER_URL } from '../../configuration';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface Booking {
  id: number;
  provider_id: string;
  username: string;
  realname: string;
  slot_time: string;
  help: boolean;
}

function getToken(): string {
  const raw = localStorage.getItem('Token');
  return raw ? JSON.parse(raw).token : '';
}

function formatDateTime(isoString: string): string {
  const d = new Date(isoString);
  return d.toLocaleDateString('sv-SE') + ' ' + d.toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' });
}

function toLocalDateString(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function isSameDate(isoString: string, dateStr: string): boolean {
  return toLocalDateString(new Date(isoString)) === dateStr;
}


export default (): JSX.Element => {
  const user = useSelector<GlobalStore, User | null>(store => store.user);

  const lastVisited = localStorage.getItem('lastVisitedQueue');
  const [selectedQueue, setSelectedQueue] = useState<string | null>(
    lastVisited && user && (user.isAdministrator || user.teacherIn.includes(lastVisited)) ? lastVisited : null
  );
  const [selectedDate, setSelectedDate] = useState<string>(toLocalDateString(new Date()));

  // Bookings state
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [bookingsLoading, setBookingsLoading] = useState(false);
  const [bookingError, setBookingError] = useState('');
  const [jsonText, setJsonText] = useState('');

  const authHeader = { Authorization: `Token ${getToken()}` };

  function loadBookings(queue: string): void {
    setBookingError('');
    fetch(`${HTTP_SERVER_URL}/api/queues/${queue}/bookings`, { headers: authHeader })
      .then(r => r.json())
      .then((data: Booking[]) => setBookings(data))
      .catch(() => setBookingError('Failed to load bookings.'));
  }

  useEffect(() => {
    if (!selectedQueue) return;
    setBookings([]);
    setJsonText('');
    loadBookings(selectedQueue);
  }, [selectedQueue]);

  function handleUpload(): void {
    if (!selectedQueue) return;
    setBookingError('');
    let parsed: any[];
    try {
      parsed = JSON.parse(jsonText);
      if (!Array.isArray(parsed)) throw new Error('Expected a JSON array');
    } catch (e) {
      setBookingError(`Invalid JSON: ${(e as any).message}`);
      return;
    }
    setBookingsLoading(true);
    fetch(`${HTTP_SERVER_URL}/api/queues/${selectedQueue}/bookings`, {
      method: 'POST',
      headers: { ...authHeader, 'Content-Type': 'application/json' },
      body: JSON.stringify(parsed)
    })
      .then(r => r.ok ? r.json() : Promise.reject(`Server error: ${r.status}`))
      .then((data: { error: string | null, bookings: Booking[], skipped: string[] }) => {
        setBookings(data.bookings);
        setJsonText('');
        if (data.error === 'unresolved_identifiers') {
          setBookingError(`${data.skipped.length} identifier(s) could not be resolved: ${data.skipped.join(', ')}. The remaining bookings were uploaded.`);
        }
      })
      .catch((e: any) => setBookingError(String(e)))
      .finally(() => setBookingsLoading(false));
  }

  function handleClear(): void {
    if (!selectedQueue) return;
    if (!window.confirm('Delete all bookings for this queue? This cannot be undone.')) return;
    setBookingsLoading(true);
    fetch(`${HTTP_SERVER_URL}/api/queues/${selectedQueue}/bookings`, {
      method: 'DELETE',
      headers: authHeader
    })
      .then(() => loadBookings(selectedQueue))
      .catch((e: any) => setBookingError(String(e)))
      .finally(() => setBookingsLoading(false));
  }

  if (user === null || !user.isTeacher()) return <PageNotFound />;

  const now = new Date();
  const today = toLocalDateString(now);
  const selectedBookings = bookings.filter(b => isSameDate(b.slot_time, selectedDate));

  const isToday = selectedDate === today;
  const selectedLabel = isToday ? 'Today' : new Date(selectedDate + 'T12:00:00').toLocaleDateString('sv-SE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const todayUpcoming = isToday ? selectedBookings.filter(b => new Date(b.slot_time) >= now) : [];
  const todayPast     = isToday ? selectedBookings.filter(b => new Date(b.slot_time) < now)  : [];

  const pastRowStyle = { backgroundColor: '#f0f0f0', color: '#888' };

  const datesWithBookings = Array.from(new Set(bookings.map(b => toLocalDateString(new Date(b.slot_time))))).map(d => new Date(d + 'T12:00:00'));

  return (
    <div className="container mt-4">

      <h2>Bookings</h2>

      <div className="alert alert-secondary mb-4" style={{ fontSize: '0.9em' }}>
        <strong>How bookings work:</strong> Upload a list of pre-booked lab slots as a JSON array.
        Each student is matched by their KTH username (e.g. <code>alba</code>) or provider ID (e.g. <code>u1abc123</code>).
        On the day of their booking, within 30 minutes of their slot, the student's queue entry
        will show a badge with their time and be promoted to the top of the queue — helping TAs prioritise them.
        Uploading again updates existing bookings and adds new ones; old bookings from previous
        sessions are kept for reference. Use "Delete all bookings" only if you need to start fresh — it only deletes upcoming bookings, past ones are kept.
      </div>

      <div className="mb-4" style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
        {user.teacherIn.map(q => (
          <button
            key={q}
            type="button"
            className={`btn btn-sm ${selectedQueue === q ? 'btn-primary' : 'btn-outline-secondary'}`}
            onClick={() => setSelectedQueue(q)}
          >
            {q}
          </button>
        ))}
      </div>

      {selectedQueue && (
        <>
          {/* Date picker */}
          <style>{`.react-datepicker__day--highlighted, .react-datepicker__day--highlighted:hover { font-weight: bold !important; color: #fff !important; background-color: #0056b3 !important; border-radius: 50%; }`}</style>
          <div className="d-flex align-items-center mb-3" style={{ gap: '0.75rem' }}>
            <label className="mb-0 font-weight-bold">Date:</label>
            <DatePicker
              selected={new Date(selectedDate + 'T12:00:00')}
              onChange={(date: Date | null) => date && setSelectedDate(toLocalDateString(date))}
              dateFormat="yyyy-MM-dd"
              className="form-control"
              highlightDates={datesWithBookings}
            />
            {!isToday && (
              <button className="btn btn-sm btn-outline-secondary" onClick={() => setSelectedDate(today)}>
                Back to today
              </button>
            )}
          </div>

          {/* Selected date */}
          {isToday ? (
            <>
              {selectedBookings.length === 0
                ? <p className="text-muted">No bookings for today.</p>
                : <>
                    {todayUpcoming.length > 0 && (
                      <>
                        <h5>Upcoming ({todayUpcoming.length})</h5>
                        <table className="table table-sm table-bordered mb-4">
                          <thead className="thead-dark">
                            <tr><th>Name</th><th>Username</th><th>Time</th><th>Type</th></tr>
                          </thead>
                          <tbody>
                            {todayUpcoming.map(b => (
                              <tr key={b.id}>
                                <td>{b.realname}</td>
                                <td>{b.username}</td>
                                <td>{formatDateTime(b.slot_time)}</td>
                                <td>{b.help ? 'help' : 'present'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                    {todayPast.length > 0 && (
                      <>
                        <h5 className="text-muted">Past ({todayPast.length})</h5>
                        <table className="table table-sm table-bordered mb-4">
                          <thead className="thead-dark">
                            <tr><th>Name</th><th>Username</th><th>Time</th><th>Type</th></tr>
                          </thead>
                          <tbody>
                            {todayPast.map(b => (
                              <tr key={b.id} style={pastRowStyle}>
                                <td>{b.realname}</td>
                                <td>{b.username}</td>
                                <td>{formatDateTime(b.slot_time)}</td>
                                <td>{b.help ? 'help' : 'present'}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </>
                    )}
                  </>
              }
            </>
          ) : (
            <>
              <h5>{selectedLabel} ({selectedBookings.length})</h5>
              {selectedBookings.length === 0
                ? <p className="text-muted">No bookings for this date.</p>
                : <table className="table table-sm table-bordered mb-4">
                    <thead className="thead-dark">
                      <tr><th>Name</th><th>Username</th><th>Time</th><th>Type</th></tr>
                    </thead>
                    <tbody>
                      {selectedBookings.map(b => (
                        <tr key={b.id}>
                          <td>{b.realname}</td>
                          <td>{b.username}</td>
                          <td>{formatDateTime(b.slot_time)}</td>
                          <td>{b.help ? 'help' : 'present'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
              }
            </>
          )}

          {/* Upload */}
          <h5>Upload bookings</h5>
          <p className="text-muted" style={{ fontSize: '0.85em' }}>
            Paste a JSON array. Each object needs <code>username</code> (e.g. <code>alba</code>) or <code>provider_id</code> (e.g. <code>u1abc123</code>),
            and <code>slot_time</code> in ISO 8601. <code>entry_type</code> is optional and defaults to <code>"present"</code>; use <code>"help"</code> for help slots.
            For <code>slot_time</code>: use <code>Z</code> for UTC, an explicit offset (e.g. <code>+02:00</code>), or omit the timezone entirely to use Stockholm local time.
            Existing bookings for the same student are updated; all others are kept.
          </p>
          <textarea
            className="w-100 form-control mb-2"
            rows={5}
            placeholder={'[\n  {"username": "alba", "slot_time": "2026-03-01T10:00:00"},\n  {"provider_id": "u1abc123", "slot_time": "2026-03-01T10:15:00Z", "entry_type": "help"},\n  {"username": "bob", "slot_time": "2026-03-01T10:30:00+02:00"}\n]'}
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
          />
          {bookingError && <p className="text-danger">{bookingError}</p>}
          <div className="d-flex mb-5" style={{ gap: '0.5rem' }}>
            <button
              className="btn btn-primary"
              onClick={handleUpload}
              disabled={bookingsLoading || !jsonText.trim()}>
              {bookingsLoading ? 'Uploading…' : 'Upload bookings'}
            </button>
            <button
              className="btn btn-outline-secondary"
              onClick={() => loadBookings(selectedQueue)}
              disabled={bookingsLoading}>
              Reload bookings
            </button>
            <button
              className="btn btn-outline-danger ml-auto"
              onClick={handleClear}
              disabled={bookingsLoading}>
              Delete all bookings
            </button>
          </div>
        </>
      )}

      <p className="text-muted" style={{ fontSize: '0.85em' }}>
        Bookings can also be managed via the REST API. For a working example that pulls from Canvas, see <a href="/examples/canvas_to_queue.py" download><code>canvas_to_queue.py</code></a>.
        There is more information about the REST API on the <Link to="/api">API</Link> tab.
      </p>

    </div>
  );
};
